import axios from 'axios';
import React, { useState, useEffect } from 'react';

interface Modifier {
  id: string;
  name: string;
  available: boolean;
  price: string;
}

interface ModifierGroup {
  id: string;
  name: string;
  min_required: number;
  max_allowed: number;
  available: boolean;
  is_combo: boolean;
  is_variant_group: boolean;
  modifiers: Modifier[];
}

interface MenuItem {
  id: string;
  name: string;
  description: string;
  price: string;
  available: boolean;
  snoozed: boolean;
  tags: string[];
  modifier_groups: ModifierGroup[];
  is_combo: boolean;
}

interface Category {
  id: string;
  name: string;
  description: string;
  available: boolean;
  items: MenuItem[];
}

interface MenuData {
  id: number;
  name: string;
  store_name: string;
  categories: Category[];
}

interface RegularHours {
  day: string;
  status: string;
  hours: string;
}

interface SpecialHours {
  date: string;
  holiday: string;
  status: string;
  hours: string;
}

interface StoreInfo {
  store_name: string;
  website: string;
  store_address: string;
  address: string;
  takes_reservations: boolean;
  busy_mode_status: string;
  busy_mode_delay_minutes: number;
  outdoor_seating: boolean;
  parking_info: string | null;
  regular_hours: RegularHours[];
  upcoming_special_hours: SpecialHours[];
}

interface MenuResponse {
  results: Array<{
    toolCallId: string;
    result: {
      menu_data: MenuData;
    };
    dynamicVariables: {
      menu_received: string;
    };
  }>;
}

interface StoreResponse {
  results: Array<{
    toolCallId: string;
    result: {
      store_info: StoreInfo;
    };
    dynamicVariables: {
      store_info_received: string;
      busy_mode_status: string;
      busy_mode_delay_minutes: string;
    };
  }>;
}

interface MenuAvailabilityTime {
  day_of_week: string;
  start_time: string;
  end_time: string;
}

interface MenuAvailability {
  menu_id: number;
  menu_name: string;
  store_name: string;
  store_address: string;
  timezone: string | null;
  busy_mode_status: string;
  busy_mode_delay_minutes: number | null;
  availabilities: {
    [key: string]: MenuAvailabilityTime[];
  };
}

interface MenuAvailabilityResponse {
  results: Array<{
    toolCallId: string;
    result: {
      menu_availability: MenuAvailability;
    };
    dynamicVariables: {
      menu_availability_received: string;
    };
  }>;
}

interface OrderSubmission {
  customerName: string;
  customerPhone: string;
  storePhone: string;
  quantity: number;
  burgerSelection: string;
  friesSelection: string[];
  orderType: 'delivery' | 'pickup';
  payOnSite: boolean;
  deliveryAddress?: {
    street: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
}

interface OrderResponse {
  success: boolean;
  order?: string;
  payment_link?: string;
  error?: string;
  rawResponse?: any;
}

interface ModifierSelection {
  modifierId: string;
  quantity: number;
}

interface TexasBurgerOrder {
  customerName: string;
  customerPhone: string;
  storePhone: string;
  quantity: number;
  ingredients: ModifierSelection[];
  orderType: 'delivery' | 'pickup';
  payOnSite: boolean;
  deliveryAddress?: {
    street: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
}

const TestingPage: React.FC = () => {
  const [menuData, setMenuData] = useState<MenuData | null>(null);
  const [storeInfo, setStoreInfo] = useState<StoreInfo | null>(null);
  const [menuAvailability, setMenuAvailability] =
    useState<MenuAvailability | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [_selectedCategory, _setSelectedCategory] = useState<string | null>(
    null,
  );

  // Order submission state
  const [orderSubmission, setOrderSubmission] = useState<OrderSubmission>({
    customerName: 'Test Customer',
    customerPhone: '+17184007098',
    storePhone: '+18883903954',
    quantity: 1,
    burgerSelection: '998', // Cheeseburger
    friesSelection: ['1003'], // French Fries
    orderType: 'delivery',
    payOnSite: false,
    deliveryAddress: {
      street: '200 Water Street',
      city: 'New York',
      state: 'NY',
      postalCode: '10038',
      country: 'US',
    },
  });
  const [orderResponse, setOrderResponse] = useState<OrderResponse | null>(
    null,
  );
  const [submittingOrder, setSubmittingOrder] = useState(false);

  // Dynamic modifier group IDs for Burger Combo
  const [modifierGroups, setModifierGroups] = useState<{
    burgerGroupId: string | null;
    friesGroupId: string | null;
    burgerModifiers: Modifier[];
    friesModifiers: Modifier[];
  }>({
    burgerGroupId: null,
    friesGroupId: null,
    burgerModifiers: [],
    friesModifiers: [],
  });

  // Texas Burger testing state
  const [texasBurgerOrder, setTexasBurgerOrder] = useState<TexasBurgerOrder>({
    customerName: 'Test Customer',
    customerPhone: '+17184007098',
    storePhone: '+18883903954',
    quantity: 1,
    ingredients: [],
    orderType: 'delivery',
    payOnSite: false,
    deliveryAddress: {
      street: '200 Water Street',
      city: 'New York',
      state: 'NY',
      postalCode: '10038',
      country: 'US',
    },
  });
  const [texasBurgerResponse, setTexasBurgerResponse] =
    useState<OrderResponse | null>(null);
  const [submittingTexasBurger, setSubmittingTexasBurger] = useState(false);
  const [texasBurgerIngredients, setTexasBurgerIngredients] = useState<
    Modifier[]
  >([]);
  const [texasBurgerGroupId, setTexasBurgerGroupId] = useState<string | null>(
    null,
  );
  const [texasBurgerItemId, setTexasBurgerItemId] = useState<string | null>(
    null,
  );

  // Extract modifier groups for Burger Combo (Item 135) and Texas Burger
  const extractModifierGroups = (menuData: MenuData) => {
    const burgerCombo = menuData.categories
      .flatMap((cat) => cat.items)
      .find((item) => item.id === '135'); // Burger Combo

    if (!burgerCombo) {
      console.warn('Burger Combo (Item 135) not found in menu data');
      return;
    }

    // Find Burger Selection group (the one with modifiers)
    const burgerGroup = burgerCombo.modifier_groups.find(
      (group) =>
        group.name === 'Burger Selection' && group.modifiers.length > 0,
    );

    // Find French Fries Selection group (the one with modifiers)
    const friesGroup = burgerCombo.modifier_groups.find(
      (group) =>
        group.name === 'French Fries Selection' && group.modifiers.length > 0,
    );

    if (burgerGroup && friesGroup) {
      setModifierGroups({
        burgerGroupId: burgerGroup.id,
        friesGroupId: friesGroup.id,
        burgerModifiers: burgerGroup.modifiers,
        friesModifiers: friesGroup.modifiers,
      });

      // Update the order submission with the first available burger and fries
      setOrderSubmission((prev) => ({
        ...prev,
        burgerSelection: burgerGroup.modifiers[0]?.id || prev.burgerSelection,
        friesSelection: friesGroup.modifiers[0]?.id
          ? [friesGroup.modifiers[0].id]
          : prev.friesSelection,
      }));
    } else {
      console.warn('Required modifier groups not found for Burger Combo');
      console.log(
        'Available groups:',
        burgerCombo.modifier_groups.map((g) => ({
          id: g.id,
          name: g.name,
          hasModifiers: g.modifiers.length > 0,
        })),
      );
    }

    // Extract Texas Burger dynamically by name
    const texasBurger = menuData.categories
      .flatMap((cat) => cat.items)
      .find((item) => item.name.toLowerCase().includes('texas')); // Find by name

    if (texasBurger) {
      setTexasBurgerItemId(texasBurger.id);

      // Find Ingredients modifier group
      const ingredientsGroup = texasBurger.modifier_groups.find(
        (group) => group.name === 'Ingredients' && group.modifiers.length > 0,
      );

      if (ingredientsGroup) {
        setTexasBurgerGroupId(ingredientsGroup.id);
        setTexasBurgerIngredients(ingredientsGroup.modifiers);
        console.log('Texas Burger found:', {
          itemId: texasBurger.id,
          itemName: texasBurger.name,
          groupId: ingredientsGroup.id,
          groupName: ingredientsGroup.name,
          maxAllowed: ingredientsGroup.max_allowed,
          modifiersCount: ingredientsGroup.modifiers.length,
        });
      } else {
        console.warn('Ingredients modifier group not found for Texas Burger');
        console.log(
          'Available groups for Texas Burger:',
          texasBurger.modifier_groups.map((g) => ({
            id: g.id,
            name: g.name,
            hasModifiers: g.modifiers.length > 0,
            maxAllowed: g.max_allowed,
          })),
        );
      }
    } else {
      console.warn(
        'Texas Burger not found in menu data. Looking for items with "texas" in the name.',
      );
      console.log(
        'Available items:',
        menuData.categories
          .flatMap((cat) => cat.items)
          .map((item) => ({
            id: item.id,
            name: item.name,
            category: menuData.categories.find((cat) =>
              cat.items.includes(item),
            )?.name,
          })),
      );
    }
  };

  const fetchMenuData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post<MenuResponse>(
        `${process.env.REACT_APP_API_BASE_URL}/api/get-menu-data`,
        {
          call: {
            retell_llm_dynamic_variables: {
              'toolCalls.0.id': 'tool_call_test_1',
              'message.phoneNumber.number': '+18883903954',
              'message.customer.number': '+17184007098',
            },
          },
        },
      );

      if (response.data.results && response.data.results.length > 0) {
        const menuData = response.data.results[0].result.menu_data;
        setMenuData(menuData);
        // Extract modifier groups for Burger Combo
        extractModifierGroups(menuData);
      } else {
        setError('No menu data received');
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to fetch menu data',
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchStoreInfo = async () => {
    try {
      const response = await axios.post<StoreResponse>(
        `${process.env.REACT_APP_API_BASE_URL}/api/get-store-info`,
        {
          call: {
            retell_llm_dynamic_variables: {
              'toolCalls.0.id': 'tool_call_test_2',
              'message.phoneNumber.number': '+18883903954',
              'message.customer.number': '+17184007098',
            },
          },
        },
      );

      if (response.data.results && response.data.results.length > 0) {
        setStoreInfo(response.data.results[0].result.store_info);
      }
    } catch (err) {
      console.error('Failed to fetch store info:', err);
    }
  };

  const fetchMenuAvailability = async () => {
    try {
      const response = await axios.post<MenuAvailabilityResponse>(
        `${process.env.REACT_APP_API_BASE_URL}/api/menu/menu-availability`,
        {
          call: {
            retell_llm_dynamic_variables: {
              'toolCalls.0.id': 'tool_call_test_2',
              'message.phoneNumber.number': '+18883903954',
              'message.customer.number': '+17184007098',
            },
          },
        },
      );

      if (response.data.results && response.data.results.length > 0) {
        setMenuAvailability(response.data.results[0].result.menu_availability);
      }
    } catch (err) {
      console.error('Failed to fetch menu availability:', err);
    }
  };

  const submitTestOrder = async () => {
    setSubmittingOrder(true);
    setOrderResponse(null);

    try {
      const orderPayload = {
        toolCalls: [
          {
            id: 'tool_call_test_order',
            functionArguments: {
              customerName: orderSubmission.customerName,
              customerPhone: orderSubmission.customerPhone,
              storePhone: orderSubmission.storePhone,
              itemIds: [
                {
                  menu_item_id: 135, // Burger Combo
                  quantity: orderSubmission.quantity,
                  modifiers: [
                    ...(modifierGroups.burgerGroupId
                      ? [
                          {
                            group_id: parseInt(modifierGroups.burgerGroupId),
                            selections: [
                              parseInt(orderSubmission.burgerSelection),
                            ],
                          },
                        ]
                      : []),
                    ...(modifierGroups.friesGroupId
                      ? [
                          {
                            group_id: parseInt(modifierGroups.friesGroupId),
                            selections: orderSubmission.friesSelection.map(
                              (id) => parseInt(id),
                            ),
                          },
                        ]
                      : []),
                  ],
                },
              ],
              payOnSite: orderSubmission.payOnSite,
              orderType: orderSubmission.orderType,
              ...(orderSubmission.orderType === 'delivery' &&
                orderSubmission.deliveryAddress && {
                  deliveryAddress: orderSubmission.deliveryAddress,
                }),
            },
          },
        ],
        call: {
          retell_llm_dynamic_variables: {
            'toolCalls.0.id': 'tool_call_test_order',
            'message.phoneNumber.number': orderSubmission.storePhone,
            'message.customer.number': orderSubmission.customerPhone,
          },
        },
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/submit-order/`,
        orderPayload,
      );

      if (response.data.error) {
        setOrderResponse({
          success: false,
          error: response.data.error,
          rawResponse: response.data,
        });
      } else if (response.data.results && response.data.results.length > 0) {
        const result = response.data.results[0].result;
        setOrderResponse({
          success: true,
          order: result.order,
          payment_link: result.payment_link,
          rawResponse: response.data,
        });
      }
    } catch (err: any) {
      setOrderResponse({
        success: false,
        error:
          err.response?.data?.error || err.message || 'Failed to submit order',
        rawResponse: err.response?.data,
      });
    } finally {
      setSubmittingOrder(false);
    }
  };

  const submitTexasBurgerOrder = async () => {
    setSubmittingTexasBurger(true);
    setTexasBurgerResponse(null);

    try {
      // Build modifiers array with quantities
      const modifiers = [];
      if (texasBurgerGroupId) {
        const selections = [];
        for (const ingredient of texasBurgerOrder.ingredients) {
          for (let i = 0; i < ingredient.quantity; i++) {
            selections.push(parseInt(ingredient.modifierId));
          }
        }

        if (selections.length > 0) {
          modifiers.push({
            group_id: parseInt(texasBurgerGroupId),
            selections: selections,
          });
        }
      }

      const orderPayload = {
        toolCalls: [
          {
            id: 'tool_call_texas_burger_order',
            functionArguments: {
              customerName: texasBurgerOrder.customerName,
              customerPhone: texasBurgerOrder.customerPhone,
              storePhone: texasBurgerOrder.storePhone,
              itemIds: [
                {
                  menu_item_id: parseInt(texasBurgerItemId || '0'), // Texas Burger (dynamic)
                  quantity: texasBurgerOrder.quantity,
                  modifiers: modifiers,
                },
              ],
              payOnSite: texasBurgerOrder.payOnSite,
              orderType: texasBurgerOrder.orderType,
              ...(texasBurgerOrder.orderType === 'delivery' &&
                texasBurgerOrder.deliveryAddress && {
                  deliveryAddress: texasBurgerOrder.deliveryAddress,
                }),
            },
          },
        ],
        call: {
          retell_llm_dynamic_variables: {
            'toolCalls.0.id': 'tool_call_texas_burger_order',
            'message.phoneNumber.number': texasBurgerOrder.storePhone,
            'message.customer.number': texasBurgerOrder.customerPhone,
          },
        },
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/submit-order/`,
        orderPayload,
      );

      if (response.data.error) {
        setTexasBurgerResponse({
          success: false,
          error: response.data.error,
          rawResponse: response.data,
        });
      } else if (response.data.results && response.data.results.length > 0) {
        const result = response.data.results[0].result;
        setTexasBurgerResponse({
          success: true,
          order: result.order,
          payment_link: result.payment_link,
          rawResponse: response.data,
        });
      }
    } catch (err: any) {
      setTexasBurgerResponse({
        success: false,
        error:
          err.response?.data?.error || err.message || 'Failed to submit order',
        rawResponse: err.response?.data,
      });
    } finally {
      setSubmittingTexasBurger(false);
    }
  };

  useEffect(() => {
    fetchMenuData();
    fetchStoreInfo();
    fetchMenuAvailability();
  }, []);

  const formatPrice = (price: string) => {
    const priceNum = parseInt(price);
    return `$${(priceNum / 100).toFixed(2)}`;
  };

  // Helper functions for Texas Burger ingredients
  const addIngredient = (modifierId: string) => {
    setTexasBurgerOrder((prev) => ({
      ...prev,
      ingredients: [...prev.ingredients, { modifierId, quantity: 1 }],
    }));
  };

  const removeIngredient = (index: number) => {
    setTexasBurgerOrder((prev) => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index),
    }));
  };

  const updateIngredientQuantity = (index: number, quantity: number) => {
    setTexasBurgerOrder((prev) => ({
      ...prev,
      ingredients: prev.ingredients.map((ingredient, i) =>
        i === index ? { ...ingredient, quantity } : ingredient,
      ),
    }));
  };

  const renderModifierGroup = (group: ModifierGroup) => (
    <div
      key={group.id}
      className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
    >
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium text-gray-900 dark:text-white">
          {group.name}
        </h4>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {group.min_required > 0 && `Min: ${group.min_required}`}
          {group.max_allowed > 0 && ` | Max: ${group.max_allowed}`}
          {group.is_combo && <span className="ml-2 text-blue-600">Combo</span>}
          {group.is_variant_group && (
            <span className="ml-2 text-green-600">Variant</span>
          )}
        </div>
      </div>
      {group.modifiers.length > 0 ? (
        <div className="space-y-2">
          {group.modifiers.map((modifier) => (
            <div
              key={modifier.id}
              className="flex items-center justify-between p-2 bg-white dark:bg-gray-700 rounded border"
            >
              <span className="text-sm">{modifier.name}</span>
              <div className="flex items-center gap-2">
                {modifier.price !== '0' && (
                  <span className="text-sm font-medium text-green-600">
                    +{formatPrice(modifier.price)}
                  </span>
                )}
                <span
                  className={`text-xs px-2 py-1 rounded ${
                    modifier.available
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  }`}
                >
                  {modifier.available ? 'Available' : 'Unavailable'}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500 dark:text-gray-400 italic">
          No modifiers available
        </p>
      )}
    </div>
  );

  const renderMenuItem = (item: MenuItem) => (
    <div
      key={item.id}
      className="mb-6 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {item.name}
          </h3>
          {item.description && (
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              {item.description}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 ml-4">
          <span className="text-lg font-bold text-green-600">
            {formatPrice(item.price)}
          </span>
          <span
            className={`text-xs px-2 py-1 rounded ${
              item.available
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
            }`}
          >
            {item.available ? 'Available' : 'Unavailable'}
          </span>
          {item.snoozed && (
            <span className="text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
              Snoozed
            </span>
          )}
          {item.is_combo && (
            <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
              Combo
            </span>
          )}
        </div>
      </div>

      {item.tags && item.tags.length > 0 && (
        <div className="mb-3">
          <div className="flex flex-wrap gap-1">
            {item.tags.map((tag, index) => (
              <span
                key={index}
                className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {item.modifier_groups && item.modifier_groups.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Modifier Groups:
          </h4>
          <div className="space-y-3">
            {item.modifier_groups.map(renderModifierGroup)}
          </div>
        </div>
      )}
    </div>
  );

  const renderCategory = (category: Category) => {
    const availableItems = category.items.filter(
      (item) => item.available,
    ).length;
    const snoozedItems = category.items.filter((item) => item.snoozed).length;
    const comboItems = category.items.filter((item) => item.is_combo).length;

    return (
      <div key={category.id} className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {category.name}
          </h2>
          <div className="flex items-center gap-2">
            <span
              className={`text-xs px-2 py-1 rounded ${
                category.available
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
              }`}
            >
              {category.available ? 'Available' : 'Unavailable'}
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {category.items.length} items
            </span>
          </div>
        </div>

        {/* Category Availability Stats */}
        <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-green-600 font-medium">
                {availableItems}
              </span>
              <span className="text-gray-600 dark:text-gray-400">
                available
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-red-600 font-medium">
                {category.items.length - availableItems}
              </span>
              <span className="text-gray-600 dark:text-gray-400">
                unavailable
              </span>
            </div>
            {snoozedItems > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-yellow-600 font-medium">
                  {snoozedItems}
                </span>
                <span className="text-gray-600 dark:text-gray-400">
                  snoozed
                </span>
              </div>
            )}
            {comboItems > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-blue-600 font-medium">{comboItems}</span>
                <span className="text-gray-600 dark:text-gray-400">
                  combo items
                </span>
              </div>
            )}
          </div>
        </div>

        {category.description && (
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            {category.description}
          </p>
        )}

        <div className="grid gap-4">{category.items.map(renderMenuItem)}</div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">
            Loading menu data...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Error Loading Menu
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">{error}</p>
          <button
            onClick={fetchMenuData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!menuData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-300">
            No menu data available
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Deliverect Testing - Menu Display
        </h1>
        <p className="text-gray-600 dark:text-gray-300">
          Testing interface for Deliverect certification - Menu functionality
        </p>
      </div>

      <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100">
              {menuData.store_name}
            </h3>
            <p className="text-blue-700 dark:text-blue-300">
              Menu: {menuData.name}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={fetchMenuData}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Refresh Menu
            </button>
            <button
              onClick={fetchStoreInfo}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Refresh Store Info
            </button>
            <button
              onClick={fetchMenuAvailability}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              Refresh Menu Availability
            </button>
          </div>
        </div>
      </div>

      {/* Menu Availability Section */}
      <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
        <h3 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-3">
          Menu Availability Status
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Available Categories
              </span>
              <span className="text-lg font-bold text-green-600">
                {menuData.categories.filter((cat) => cat.available).length}
              </span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              of {menuData.categories.length} total
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Available Items
              </span>
              <span className="text-lg font-bold text-green-600">
                {menuData.categories.reduce(
                  (total, cat) =>
                    total + cat.items.filter((item) => item.available).length,
                  0,
                )}
              </span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              of{' '}
              {menuData.categories.reduce(
                (total, cat) => total + cat.items.length,
                0,
              )}{' '}
              total
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Snoozed Items
              </span>
              <span className="text-lg font-bold text-yellow-600">
                {menuData.categories.reduce(
                  (total, cat) =>
                    total + cat.items.filter((item) => item.snoozed).length,
                  0,
                )}
              </span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              temporarily unavailable
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Combo Items
              </span>
              <span className="text-lg font-bold text-blue-600">
                {menuData.categories.reduce(
                  (total, cat) =>
                    total + cat.items.filter((item) => item.is_combo).length,
                  0,
                )}
              </span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              meal deals available
            </div>
          </div>
        </div>
      </div>

      {/* Store Information Section */}
      {storeInfo && (
        <div className="mb-6 p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
          <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-100 mb-3">
            Store Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border">
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Store Name
              </div>
              <div className="text-lg font-bold text-gray-900 dark:text-white">
                {storeInfo.store_name}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border">
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Address
              </div>
              <div className="text-sm text-gray-900 dark:text-white">
                {storeInfo.store_address}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border">
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Website
              </div>
              <div className="text-sm text-blue-600 dark:text-blue-400">
                <a
                  href={storeInfo.website}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {storeInfo.website}
                </a>
              </div>
            </div>
          </div>

          {/* Busy Mode Status */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                Busy Mode Status
              </h4>
              <span
                className={`text-sm px-3 py-1 rounded-full ${
                  storeInfo.busy_mode_status === 'BUSY'
                    ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                }`}
              >
                {storeInfo.busy_mode_status}
              </span>
            </div>

            {storeInfo.busy_mode_status === 'BUSY' && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <span className="text-red-600 text-lg">⏰</span>
                  <div>
                    <div className="text-sm font-medium text-red-800 dark:text-red-200">
                      Busy Mode Active
                    </div>
                    <div className="text-sm text-red-600 dark:text-red-300">
                      Additional delay: {storeInfo.busy_mode_delay_minutes}{' '}
                      minutes
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Store Features */}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white dark:bg-gray-800 p-3 rounded border text-center">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Reservations
              </div>
              <div
                className={`text-sm font-medium ${
                  storeInfo.takes_reservations
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-red-600 dark:text-red-400'
                }`}
              >
                {storeInfo.takes_reservations ? 'Yes' : 'No'}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-3 rounded border text-center">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Outdoor Seating
              </div>
              <div
                className={`text-sm font-medium ${
                  storeInfo.outdoor_seating
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-red-600 dark:text-red-400'
                }`}
              >
                {storeInfo.outdoor_seating ? 'Yes' : 'No'}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-3 rounded border text-center">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Parking
              </div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {storeInfo.parking_info || 'Not specified'}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-3 rounded border text-center">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Busy Mode
              </div>
              <div
                className={`text-sm font-medium ${
                  storeInfo.busy_mode_status === 'BUSY'
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-green-600 dark:text-green-400'
                }`}
              >
                {storeInfo.busy_mode_status}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Menu Availability Section */}
      {menuAvailability && (
        <div className="mb-6 p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
          <h3 className="text-lg font-semibold text-orange-900 dark:text-orange-100 mb-3">
            Menu Availability
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border">
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Menu Name
              </div>
              <div className="text-lg font-bold text-gray-900 dark:text-white">
                {menuAvailability.menu_name}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border">
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Store
              </div>
              <div className="text-sm text-gray-900 dark:text-white">
                {menuAvailability.store_name}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border">
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Busy Mode
              </div>
              <div
                className={`text-sm font-medium ${
                  menuAvailability.busy_mode_status === 'ONLINE'
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-red-600 dark:text-red-400'
                }`}
              >
                {menuAvailability.busy_mode_status}
              </div>
            </div>
          </div>

          {/* Busy Mode Status from Menu Availability */}
          {menuAvailability.busy_mode_status !== 'ONLINE' && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="text-red-600 text-lg">⚠️</span>
                <div>
                  <div className="text-sm font-medium text-red-800 dark:text-red-200">
                    Menu Not Available
                  </div>
                  <div className="text-sm text-red-600 dark:text-red-300">
                    Status: {menuAvailability.busy_mode_status}
                    {menuAvailability.busy_mode_delay_minutes && (
                      <span>
                        {' '}
                        - Delay: {menuAvailability.busy_mode_delay_minutes}{' '}
                        minutes
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Weekly Availability Schedule */}
          <div className="mt-4">
            <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-3">
              Weekly Schedule
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {Object.entries(menuAvailability.availabilities).map(
                ([day, times]) => (
                  <div
                    key={day}
                    className="bg-white dark:bg-gray-800 p-3 rounded-lg border"
                  >
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {day}
                    </div>
                    {times.length > 0 ? (
                      <div className="space-y-1">
                        {times.map((time, index) => (
                          <div
                            key={index}
                            className="text-sm text-gray-900 dark:text-white"
                          >
                            <span className="font-medium">
                              {time.start_time}
                            </span>{' '}
                            -{' '}
                            <span className="font-medium">{time.end_time}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-red-600 dark:text-red-400">
                        Closed
                      </div>
                    )}
                  </div>
                ),
              )}
            </div>
          </div>

          {/* Timezone Information */}
          {menuAvailability.timezone && (
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <span className="font-medium">Timezone:</span>{' '}
                {menuAvailability.timezone}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Order Submission Testing Section */}
      <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
        <h3 className="text-lg font-semibold text-red-900 dark:text-red-100 mb-4">
          🧪 Order Submission Testing - Burger Combo Only
        </h3>
        <p className="text-sm text-red-700 dark:text-red-300 mb-4">
          Test order submission with Burger Combo (Item 135). Edit quantities
          and modifiers to test validation limits.
        </p>

        {/* Debug Info */}
        {modifierGroups.burgerGroupId && modifierGroups.friesGroupId && (
          <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="text-sm text-yellow-800 dark:text-yellow-200">
              <strong>Current Modifier Groups:</strong>
              <div className="mt-1">
                • Burger Selection: Group {modifierGroups.burgerGroupId} (
                {modifierGroups.burgerModifiers.length} options)
              </div>
              <div>
                • Fries Selection: Group {modifierGroups.friesGroupId} (
                {modifierGroups.friesModifiers.length} options)
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Order Form */}
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border">
            <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
              Order Configuration
            </h4>

            <div className="space-y-4">
              {/* Customer Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Customer Name
                  </label>
                  <input
                    type="text"
                    value={orderSubmission.customerName}
                    onChange={(e) =>
                      setOrderSubmission((prev) => ({
                        ...prev,
                        customerName: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Customer Phone
                  </label>
                  <input
                    type="text"
                    value={orderSubmission.customerPhone}
                    onChange={(e) =>
                      setOrderSubmission((prev) => ({
                        ...prev,
                        customerPhone: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              {/* Quantity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Quantity (Test min/max limits)
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={orderSubmission.quantity}
                  onChange={(e) =>
                    setOrderSubmission((prev) => ({
                      ...prev,
                      quantity: parseInt(e.target.value) || 1,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {/* Burger Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Burger Selection (Required - Group{' '}
                  {modifierGroups.burgerGroupId || 'Loading...'})
                </label>
                {modifierGroups.burgerGroupId ? (
                  <select
                    value={orderSubmission.burgerSelection}
                    onChange={(e) =>
                      setOrderSubmission((prev) => ({
                        ...prev,
                        burgerSelection: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {modifierGroups.burgerModifiers.map((modifier) => (
                      <option key={modifier.id} value={modifier.id}>
                        {modifier.name}
                        {modifier.price !== '0' &&
                          ` (+${formatPrice(modifier.price)})`}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400">
                    Loading burger options...
                  </div>
                )}
              </div>

              {/* Fries Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Fries Selection (Required - Group{' '}
                  {modifierGroups.friesGroupId || 'Loading...'}) - Test Max
                  Limits
                </label>
                {modifierGroups.friesGroupId ? (
                  <div className="space-y-2">
                    {modifierGroups.friesModifiers.map((fries) => (
                      <label key={fries.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={orderSubmission.friesSelection.includes(
                            fries.id,
                          )}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setOrderSubmission((prev) => ({
                                ...prev,
                                friesSelection: [
                                  ...prev.friesSelection,
                                  fries.id,
                                ],
                              }));
                            } else {
                              setOrderSubmission((prev) => ({
                                ...prev,
                                friesSelection: prev.friesSelection.filter(
                                  (id) => id !== fries.id,
                                ),
                              }));
                            }
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {fries.name}
                          {fries.price !== '0' &&
                            ` (+${formatPrice(fries.price)})`}
                        </span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400">
                    Loading fries options...
                  </div>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Max allowed: 1 (test by selecting multiple)
                </p>
              </div>

              {/* Order Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Order Type
                </label>
                <select
                  value={orderSubmission.orderType}
                  onChange={(e) =>
                    setOrderSubmission((prev) => ({
                      ...prev,
                      orderType: e.target.value as 'delivery' | 'pickup',
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="delivery">Delivery</option>
                  <option value="pickup">Pickup</option>
                </select>
              </div>

              {/* Pay on Site */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={orderSubmission.payOnSite}
                    onChange={(e) =>
                      setOrderSubmission((prev) => ({
                        ...prev,
                        payOnSite: e.target.checked,
                      }))
                    }
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Pay on Site
                  </span>
                </label>
              </div>

              {/* Delivery Address (if delivery) */}
              {orderSubmission.orderType === 'delivery' && (
                <div className="space-y-3">
                  <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Delivery Address
                  </h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <input
                        type="text"
                        placeholder="Street"
                        value={orderSubmission.deliveryAddress?.street || ''}
                        onChange={(e) =>
                          setOrderSubmission((prev) => ({
                            ...prev,
                            deliveryAddress: {
                              ...prev.deliveryAddress!,
                              street: e.target.value,
                            },
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        placeholder="City"
                        value={orderSubmission.deliveryAddress?.city || ''}
                        onChange={(e) =>
                          setOrderSubmission((prev) => ({
                            ...prev,
                            deliveryAddress: {
                              ...prev.deliveryAddress!,
                              city: e.target.value,
                            },
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        placeholder="State"
                        value={orderSubmission.deliveryAddress?.state || ''}
                        onChange={(e) =>
                          setOrderSubmission((prev) => ({
                            ...prev,
                            deliveryAddress: {
                              ...prev.deliveryAddress!,
                              state: e.target.value,
                            },
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        placeholder="Postal Code"
                        value={
                          orderSubmission.deliveryAddress?.postalCode || ''
                        }
                        onChange={(e) =>
                          setOrderSubmission((prev) => ({
                            ...prev,
                            deliveryAddress: {
                              ...prev.deliveryAddress!,
                              postalCode: e.target.value,
                            },
                          }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                onClick={submitTestOrder}
                disabled={submittingOrder}
                className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {submittingOrder ? 'Submitting Order...' : 'Submit Test Order'}
              </button>
            </div>
          </div>

          {/* Response Display */}
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border">
            <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
              Order Response
            </h4>

            {orderResponse ? (
              <div className="space-y-4">
                {/* Success/Error Status */}
                <div
                  className={`p-3 rounded-lg border ${
                    orderResponse.success
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                      : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                  }`}
                >
                  <div
                    className={`font-medium ${
                      orderResponse.success
                        ? 'text-green-800 dark:text-green-200'
                        : 'text-red-800 dark:text-red-200'
                    }`}
                  >
                    {orderResponse.success
                      ? '✅ Order Successful'
                      : '❌ Order Failed'}
                  </div>
                  {orderResponse.error && (
                    <div className="text-sm text-red-600 dark:text-red-300 mt-1">
                      {orderResponse.error}
                    </div>
                  )}
                  {orderResponse.order && (
                    <div className="text-sm text-green-600 dark:text-green-300 mt-1">
                      {orderResponse.order}
                    </div>
                  )}
                </div>

                {/* Payment Link */}
                {orderResponse.payment_link && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                      Payment Link:
                    </div>
                    <a
                      href={orderResponse.payment_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 dark:text-blue-400 break-all hover:underline"
                    >
                      {orderResponse.payment_link}
                    </a>
                    <div className="mt-2">
                      <button
                        onClick={() =>
                          window.open(orderResponse.payment_link, '_blank')
                        }
                        className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
                      >
                        Open Payment Link
                      </button>
                    </div>
                  </div>
                )}

                {/* Raw Response */}
                <details className="mt-4">
                  <summary className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:text-gray-900 dark:hover:text-white">
                    View Raw Response
                  </summary>
                  <pre className="mt-2 p-3 bg-gray-100 dark:bg-gray-700 rounded text-xs overflow-auto max-h-40">
                    {JSON.stringify(orderResponse.rawResponse, null, 2)}
                  </pre>
                </details>
              </div>
            ) : (
              <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                No order submitted yet. Configure your order and click "Submit
                Test Order".
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Texas Burger Order Submission Testing Section */}
      <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
        <h3 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-4">
          🍔 Texas Burger Order Testing - Modifier Quantities
        </h3>
        <p className="text-sm text-green-700 dark:text-green-300 mb-4">
          Test order submission with Texas Burger (Item{' '}
          {texasBurgerItemId || 'Loading...'}). Add multiple ingredients with
          quantities to test validation limits (max 4 allowed).
        </p>

        {/* Debug Info */}
        {texasBurgerItemId && texasBurgerGroupId && (
          <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="text-sm text-yellow-800 dark:text-yellow-200">
              <strong>Current Texas Burger Configuration:</strong>
              <div className="mt-1">• Item ID: {texasBurgerItemId}</div>
              <div>• Ingredients Group ID: {texasBurgerGroupId}</div>
              <div>
                • Available Ingredients: {texasBurgerIngredients.length}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Order Configuration */}
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border">
            <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
              Order Configuration
            </h4>

            <div className="space-y-4">
              {/* Customer Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Customer Name
                  </label>
                  <input
                    type="text"
                    value={texasBurgerOrder.customerName}
                    onChange={(e) =>
                      setTexasBurgerOrder((prev) => ({
                        ...prev,
                        customerName: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Customer Phone
                  </label>
                  <input
                    type="text"
                    value={texasBurgerOrder.customerPhone}
                    onChange={(e) =>
                      setTexasBurgerOrder((prev) => ({
                        ...prev,
                        customerPhone: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              {/* Quantity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Quantity
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={texasBurgerOrder.quantity}
                  onChange={(e) =>
                    setTexasBurgerOrder((prev) => ({
                      ...prev,
                      quantity: parseInt(e.target.value) || 1,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {/* Order Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Order Type
                </label>
                <select
                  value={texasBurgerOrder.orderType}
                  onChange={(e) =>
                    setTexasBurgerOrder((prev) => ({
                      ...prev,
                      orderType: e.target.value as 'delivery' | 'pickup',
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="delivery">Delivery</option>
                  <option value="pickup">Pickup</option>
                </select>
              </div>

              {/* Pay on Site */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={texasBurgerOrder.payOnSite}
                    onChange={(e) =>
                      setTexasBurgerOrder((prev) => ({
                        ...prev,
                        payOnSite: e.target.checked,
                      }))
                    }
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Pay on Site
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Ingredients Selection */}
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border">
            <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
              Ingredients Selection
            </h4>

            {/* Add Ingredient */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Add Ingredient
              </label>
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    addIngredient(e.target.value);
                    e.target.value = '';
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Select an ingredient...</option>
                {texasBurgerIngredients.map((ingredient) => (
                  <option key={ingredient.id} value={ingredient.id}>
                    {ingredient.name}
                    {ingredient.price !== '0' &&
                      ` (+${formatPrice(ingredient.price)})`}
                  </option>
                ))}
              </select>
            </div>

            {/* Selected Ingredients */}
            <div className="space-y-2">
              <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Selected Ingredients ({texasBurgerOrder.ingredients.length}/∞)
              </h5>
              {texasBurgerOrder.ingredients.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                  No ingredients selected. Add ingredients above to test
                  quantity limits.
                </p>
              ) : (
                texasBurgerOrder.ingredients.map((ingredient, index) => {
                  const modifier = texasBurgerIngredients.find(
                    (m) => m.id === ingredient.modifierId,
                  );
                  return (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded border"
                    >
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {modifier?.name || 'Unknown Ingredient'}
                        </div>
                        {modifier && modifier.price !== '0' && (
                          <div className="text-sm text-green-600 dark:text-green-400">
                            +{formatPrice(modifier.price)} each
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="1"
                          max="10"
                          value={ingredient.quantity}
                          onChange={(e) =>
                            updateIngredientQuantity(
                              index,
                              parseInt(e.target.value) || 1,
                            )
                          }
                          className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center"
                        />
                        <button
                          onClick={() => removeIngredient(index)}
                          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Submit Button */}
            <button
              onClick={submitTexasBurgerOrder}
              disabled={
                submittingTexasBurger ||
                texasBurgerOrder.ingredients.length === 0
              }
              className="w-full mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {submittingTexasBurger
                ? 'Submitting Order...'
                : 'Submit Texas Burger Order'}
            </button>
          </div>
        </div>

        {/* Response Display */}
        {texasBurgerResponse && (
          <div className="mt-6 bg-white dark:bg-gray-800 p-4 rounded-lg border">
            <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
              Order Response
            </h4>

            <div className="space-y-4">
              {/* Success/Error Status */}
              <div
                className={`p-3 rounded-lg border ${
                  texasBurgerResponse.success
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                }`}
              >
                <div
                  className={`font-medium ${
                    texasBurgerResponse.success
                      ? 'text-green-800 dark:text-green-200'
                      : 'text-red-800 dark:text-red-200'
                  }`}
                >
                  {texasBurgerResponse.success
                    ? '✅ Order Successful'
                    : '❌ Order Failed'}
                </div>
                {texasBurgerResponse.error && (
                  <div className="text-sm text-red-600 dark:text-red-300 mt-1">
                    {texasBurgerResponse.error}
                  </div>
                )}
                {texasBurgerResponse.order && (
                  <div className="text-sm text-green-600 dark:text-green-300 mt-1">
                    {texasBurgerResponse.order}
                  </div>
                )}
              </div>

              {/* Payment Link */}
              {texasBurgerResponse.payment_link && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                    Payment Link:
                  </div>
                  <a
                    href={texasBurgerResponse.payment_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 dark:text-blue-400 break-all hover:underline"
                  >
                    {texasBurgerResponse.payment_link}
                  </a>
                  <div className="mt-2">
                    <button
                      onClick={() =>
                        window.open(texasBurgerResponse.payment_link, '_blank')
                      }
                      className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
                    >
                      Open Payment Link
                    </button>
                  </div>
                </div>
              )}

              {/* Raw Response */}
              <details className="mt-4">
                <summary className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:text-gray-900 dark:hover:text-white">
                  View Raw Response
                </summary>
                <pre className="mt-2 p-3 bg-gray-100 dark:bg-gray-700 rounded text-xs overflow-auto max-h-40">
                  {JSON.stringify(texasBurgerResponse.rawResponse, null, 2)}
                </pre>
              </details>
            </div>
          </div>
        )}
      </div>

      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          Menu Categories ({menuData.categories.length})
        </h2>

        <div className="grid gap-6">
          {menuData.categories.map(renderCategory)}
        </div>
      </div>

      <div className="mt-8 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Menu Summary & Availability
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div className="bg-white dark:bg-gray-700 p-3 rounded border">
            <div className="font-medium text-gray-700 dark:text-gray-300 mb-1">
              Total Categories
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {menuData.categories.length}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {menuData.categories.filter((cat) => cat.available).length}{' '}
              available
            </div>
          </div>

          <div className="bg-white dark:bg-gray-700 p-3 rounded border">
            <div className="font-medium text-gray-700 dark:text-gray-300 mb-1">
              Total Items
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {menuData.categories.reduce(
                (total, cat) => total + cat.items.length,
                0,
              )}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              across all categories
            </div>
          </div>

          <div className="bg-white dark:bg-gray-700 p-3 rounded border">
            <div className="font-medium text-gray-700 dark:text-gray-300 mb-1">
              Available Items
            </div>
            <div className="text-2xl font-bold text-green-600">
              {menuData.categories.reduce(
                (total, cat) =>
                  total + cat.items.filter((item) => item.available).length,
                0,
              )}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              ready for ordering
            </div>
          </div>

          <div className="bg-white dark:bg-gray-700 p-3 rounded border">
            <div className="font-medium text-gray-700 dark:text-gray-300 mb-1">
              Unavailable Items
            </div>
            <div className="text-2xl font-bold text-red-600">
              {menuData.categories.reduce(
                (total, cat) =>
                  total + cat.items.filter((item) => !item.available).length,
                0,
              )}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              not available for order
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="bg-white dark:bg-gray-700 p-3 rounded border">
            <div className="font-medium text-gray-700 dark:text-gray-300 mb-1">
              Snoozed Items
            </div>
            <div className="text-2xl font-bold text-yellow-600">
              {menuData.categories.reduce(
                (total, cat) =>
                  total + cat.items.filter((item) => item.snoozed).length,
                0,
              )}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              temporarily unavailable
            </div>
          </div>

          <div className="bg-white dark:bg-gray-700 p-3 rounded border">
            <div className="font-medium text-gray-700 dark:text-gray-300 mb-1">
              Combo Items
            </div>
            <div className="text-2xl font-bold text-blue-600">
              {menuData.categories.reduce(
                (total, cat) =>
                  total + cat.items.filter((item) => item.is_combo).length,
                0,
              )}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              meal deals available
            </div>
          </div>

          <div className="bg-white dark:bg-gray-700 p-3 rounded border">
            <div className="font-medium text-gray-700 dark:text-gray-300 mb-1">
              Availability Rate
            </div>
            <div className="text-2xl font-bold text-green-600">
              {Math.round(
                (menuData.categories.reduce(
                  (total, cat) =>
                    total + cat.items.filter((item) => item.available).length,
                  0,
                ) /
                  menuData.categories.reduce(
                    (total, cat) => total + cat.items.length,
                    0,
                  )) *
                  100,
              )}
              %
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              items available for order
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestingPage;
